import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LinkSimple, GraduationCap, ClipboardText, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function TaskCodesPage() {
  const [codes, setCodes] = useState(null);
  const [open, setOpen] = useState("");

  useEffect(() => {
    api.get("/task-codes").then((r) => setCodes(r.data)).catch((e) => { toast.error(formatApiError(e)); setCodes([]); });
  }, []);

  const copyTrainingLink = (token, title) => {
    const url = `${window.location.origin}/train/${token}`;
    try { if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {}); } catch {}
    toast.success("Training link copied — text or email it to the employee", { description: `${title}: ${url}`, duration: 8000 });
  };

  if (!codes) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  return (
    <div data-testid="task-codes-page">
      <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Task codes</h1>
      <p className="mt-1 max-w-2xl text-sm text-stone-500">
        Every care task code and everything that touches it — the training module, guidelines, staff who completed it, and clients whose care plan uses it.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {codes.map((c) => {
          const isOpen = open === c.code;
          const mod = c.modules[0];
          return (
            <div key={c.code} className={`rounded-lg border bg-white p-5 shadow-sm transition-all ${isOpen ? "border-primary/40 ring-1 ring-primary/20" : "border-stone-200"}`} data-testid={`task-code-${c.code}`}>
              <button onClick={() => setOpen(isOpen ? "" : c.code)} className="block w-full text-left" data-testid={`task-code-toggle-${c.code}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="font-heading text-2xl font-semibold text-primary">{c.code}</span>
                    <p className="mt-0.5 text-sm font-medium text-stone-900">{c.name}</p>
                  </div>
                  {mod ? (
                    <span className="rounded-full bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-semibold text-[#1e8449]">training ready</span>
                  ) : (
                    <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-400">no module</span>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-stone-500">
                  <span className="inline-flex items-center gap-1"><GraduationCap size={13} /> {c.completions} completion{c.completions === 1 ? "" : "s"}</span>
                  <span className="inline-flex items-center gap-1"><ClipboardText size={13} /> {c.guidelines.length} guideline{c.guidelines.length === 1 ? "" : "s"}</span>
                  <span className="inline-flex items-center gap-1"><Users size={13} /> {c.clients_using} client{c.clients_using === 1 ? "" : "s"}</span>
                </div>
                {mod && <p className="mt-2 truncate text-xs text-stone-400">{mod.title}</p>}
              </button>

              {isOpen && (
                <div className="mt-4 space-y-3 border-t border-stone-100 pt-4" data-testid={`task-code-detail-${c.code}`}>
                  {c.modules.map((m) => (
                    <div key={m.id} className="rounded-md border border-stone-200 bg-accent/40 p-3" data-testid={`task-code-module-${c.code}-${m.id}`}>
                      <p className="text-xs font-semibold text-stone-800">
                        {m.title}
                        {!m.active && <span className="ml-2 rounded-full bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-semibold text-[#8a5d14]">draft / inactive</span>}
                      </p>
                      <p className="mt-0.5 text-[11px] text-stone-500">{m.series} · {m.question_count} check question{m.question_count === 1 ? "" : "s"}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <a href={`/train/${m.token}`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground hover:bg-stone-900"
                          data-testid={`task-code-open-training-${c.code}`}>
                          Open the training
                        </a>
                        <button onClick={() => copyTrainingLink(m.token, m.title)}
                          className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-2.5 py-1 text-[11px] font-medium text-stone-600 hover:bg-stone-900 hover:text-white"
                          data-testid={`task-code-copy-link-${c.code}`}>
                          <LinkSimple size={12} /> Copy staff link
                        </button>
                      </div>
                    </div>
                  ))}
                  {c.modules.length === 0 && (
                    <p className="text-xs text-stone-400">No training module covers this code yet.</p>
                  )}
                  {c.guidelines.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Guidelines staff sign off on</p>
                      <ul className="mt-1 space-y-1">
                        {c.guidelines.map((g) => (
                          <li key={g.id}>
                            <Link to="/guidelines" className="text-xs font-medium text-primary hover:underline" data-testid={`task-code-guideline-${c.code}-${g.id}`}>{g.title}</Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-[11px] text-stone-400">
                    {c.clients_using > 0
                      ? `${c.clients_using} client${c.clients_using === 1 ? " has" : "s have"} this code in their care plan — their caregivers need this module.`
                      : "No current care plan uses this code."}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
